import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Code2, Send, Shield, FileText, Link2, Webhook, ArrowRight, ExternalLink, Terminal, Code } from 'lucide-react';
import SeoMeta from '../components/SeoMeta';
const DEVELOPER_PORTAL_URL = '/es/developers/info';

const DesarrolladoresPage = () => {
  const { t } = useTranslation(['developers', 'common']);

  const capabilities = [
    { icon: FileText, title: t('developers:cap-api'), desc: t('developers:cap-api-desc') },
    { icon: Send, title: t('developers:cap-signature'), desc: t('developers:cap-signature-desc') },
    { icon: Link2, title: t('developers:cap-links'), desc: t('developers:cap-links-desc') },
    { icon: Webhook, title: t('developers:cap-webhooks'), desc: t('developers:cap-webhooks-desc') },
    { icon: Shield, title: t('developers:cap-security'), desc: t('developers:cap-security-desc') },
    { icon: Code2, title: t('developers:cap-models'), desc: t('developers:cap-models-desc') },
  ];

  return (
    <>
      <SeoMeta
        title={t('developers:seo-title')}
        description={t('developers:seo-description')}
        canonical="https://bluecmr.com/desarrolladores"
        type="website"
        keywords="BlueCMR API, developer portal, REST API transporte, integración TMS"
        breadcrumbs={[
          { name: 'Inicio', url: 'https://bluecmr.com/' },
          { name: t('developers:breadcrumb'), url: 'https://bluecmr.com/desarrolladores' },
        ]}
      />

      <div className="min-h-screen bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 mb-6">
              <Terminal className="w-4 h-4" />
              <span className="text-sm font-medium">{t('developers:badge')}</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-5">
              {t('developers:title')}
            </h1>
            <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
              {t('developers:subtitle')}
            </p>
            <a
              href={DEVELOPER_PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-3 bg-blue-600 text-white font-semibold px-10 py-4 rounded-xl hover:bg-blue-500 transition-all text-base shadow-lg shadow-blue-600/30 hover:shadow-blue-500/40"
            >
              <Code className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
              {t('developers:cta-login')}
              <ExternalLink className="w-4 h-4 opacity-60" />
            </a>
          </div>
        </section>

        {/* Capabilities */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">
              {t('developers:features-title')}
            </h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              {t('developers:features-subtitle')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((cap, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <cap.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{cap.title}</h3>
                  <p className="text-sm text-gray-600">{cap.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              {t('developers:cta-title')}
            </h2>
            <p className="text-gray-600 mb-8">
              {t('developers:cta-subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={DEVELOPER_PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-3 bg-blue-600 text-white font-semibold px-6 py-3 rounded-xl hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/20"
              >
                <Code className="w-5 h-5" />
                {t('developers:cta-access')}
                <ExternalLink className="w-4 h-4 opacity-60" />
              </a>
              <Link
                to="/contacto?origin=integracion&profile=software"
                className="inline-flex items-center justify-center gap-2 bg-white text-gray-700 font-semibold px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors border border-gray-300"
              >
                {t('developers:cta-contact')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default DesarrolladoresPage;
